import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import commonStyles from "../../verification-service-details-component.less";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ServiceOfferingProcessingTypes } from "@enums/service-offering-processing-types";
import { SortableServiceOfferingsContainer } from "./sortable-service-offerings-container/sortable-service-offerings-container";

type RunOrderTabContainerProps = {
    isDisabled: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const RunOrderTabContainer = (props: RunOrderTabContainerProps) => {
    const { isDisabled, rpServiceConfigurationState, setRpServiceConfigurationState } = props;

    const selectedServiceOfferings = useMemo(() => {
        return rpServiceConfigurationState.serviceOfferings.filter(so => so.addedToService && so.status === EntityStatusTypes.ACTIVE);
    }, [rpServiceConfigurationState.serviceOfferings]);

    const {
        duringVerifyServiceOfferings,
        redirectServiceOfferings,
        postVerifyServiceOfferings,
        penultimateVerifyServiceOfferings,
        finalVerifyServiceOfferings,
        asynchronousServiceOfferings
    } = useMemo(() => {
        return {
            duringVerifyServiceOfferings: selectedServiceOfferings.filter(so => so.processingType === ServiceOfferingProcessingTypes.DURING_VERIFY && so.fallbackStatus ==="primary"),
            redirectServiceOfferings: selectedServiceOfferings.filter(so => so.processingType === ServiceOfferingProcessingTypes.REDIRECT && so.fallbackStatus ==="primary"),
            postVerifyServiceOfferings: selectedServiceOfferings.filter(so => so.processingType === ServiceOfferingProcessingTypes.POST_VERIFY && so.fallbackStatus ==="primary"),
            penultimateVerifyServiceOfferings: selectedServiceOfferings.filter(so => so.processingType === ServiceOfferingProcessingTypes.PENULTIMATE_VERIFY && so.fallbackStatus ==="primary"),
            finalVerifyServiceOfferings: selectedServiceOfferings.filter(so => so.processingType === ServiceOfferingProcessingTypes.FINAL_VERIFY && so.fallbackStatus ==="primary"),
            asynchronousServiceOfferings: selectedServiceOfferings.filter(so => so.processingType === ServiceOfferingProcessingTypes.ASYNCHRONOUS && so.fallbackStatus ==="primary")
        };
    }, [selectedServiceOfferings]);

    const setServiceOfferingsForProcessingType = (serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>, processingType: ServiceOfferingProcessingTypes) => {
        const newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(originalServiceOffering => {
            if (originalServiceOffering.processingType === processingType) {
                const newServiceOffering = serviceOfferings.find(so => so.uuid === originalServiceOffering.uuid);
                if (newServiceOffering) {
                    return newServiceOffering
                } 
            }

            return originalServiceOffering;
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            serviceOfferings: newServiceOfferings
        });
    };

    return (
        <div>
            <div>
                <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.title} />
                <ContentPageBody isMaxWidth={true}>
                    <span className={commonStyles.description}>{resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.description}</span>
                    {duringVerifyServiceOfferings.length > 1 && (
                        <SortableServiceOfferingsContainer
                            isDisabled={isDisabled}
                            processingType={ServiceOfferingProcessingTypes.DURING_VERIFY}
                            processingTypeTitle={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.sections.duringVerify.title}
                            serviceOfferings={duringVerifyServiceOfferings}
                            setServiceOfferingsForProcessingType={setServiceOfferingsForProcessingType}
                        />
                    )}
                    {redirectServiceOfferings.length > 1 && (
                        <SortableServiceOfferingsContainer
                            isDisabled={isDisabled}
                            processingType={ServiceOfferingProcessingTypes.REDIRECT}
                            processingTypeTitle={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.sections.redirect.title}
                            serviceOfferings={redirectServiceOfferings}
                            setServiceOfferingsForProcessingType={setServiceOfferingsForProcessingType}
                        />
                    )}
                    {postVerifyServiceOfferings.length > 1 && (
                        <SortableServiceOfferingsContainer
                            isDisabled={isDisabled}
                            processingType={ServiceOfferingProcessingTypes.POST_VERIFY}
                            processingTypeTitle={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.sections.postVerify.title}
                            serviceOfferings={postVerifyServiceOfferings}
                            setServiceOfferingsForProcessingType={setServiceOfferingsForProcessingType}
                        />
                    )}
                    {penultimateVerifyServiceOfferings.length > 1 && (
                        <SortableServiceOfferingsContainer
                            isDisabled={isDisabled}
                            processingType={ServiceOfferingProcessingTypes.PENULTIMATE_VERIFY}
                            processingTypeTitle={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.sections.penultimateVerify.title}
                            serviceOfferings={penultimateVerifyServiceOfferings}
                            setServiceOfferingsForProcessingType={setServiceOfferingsForProcessingType}
                        />
                    )}
                    {finalVerifyServiceOfferings.length > 1 && (
                        <SortableServiceOfferingsContainer
                            isDisabled={isDisabled}
                            processingType={ServiceOfferingProcessingTypes.FINAL_VERIFY}
                            processingTypeTitle={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.sections.finalVerify.title}
                            serviceOfferings={finalVerifyServiceOfferings}
                            setServiceOfferingsForProcessingType={setServiceOfferingsForProcessingType}
                        />
                    )}
                    {asynchronousServiceOfferings.length > 1 && (
                        <SortableServiceOfferingsContainer
                            isDisabled={isDisabled}
                            processingType={ServiceOfferingProcessingTypes.ASYNCHRONOUS}
                            processingTypeTitle={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.sections.asynchronous.title}
                            serviceOfferings={asynchronousServiceOfferings}
                            setServiceOfferingsForProcessingType={setServiceOfferingsForProcessingType}
                        />
                    )}
                </ContentPageBody>
            </div>
        </div>
    );
};